/**
 * Deterministic seed data for the mock engine. Forty people with enough variety that every screen has something to show.
 */
import type {
  Contact,
  ContactProfile,
  ContactStatistics,
  Group,
  Id,
  Integration,
  InteractionType,
  Lookup,
  ContactLookup,
  MilestoneDate,
  Note,
  Reminder,
  ReconnectCadence,
  RelatedContact,
  RelatedContactLabel,
  User,
  Workspace,
} from "@hub/shared";
import type { Need, Service } from "@hub/shared";

export const MOCK_WORKSPACE_ID = "ws-mock-0001";
export const MOCK_USER_ID = "user-mock-0001";

export const mockUser: User = {
  id: MOCK_USER_ID,
  email: "you@example.com",
  username: "you",
  firstName: "Sam",
  lastName: "Rivera",
  timezone: "America/Los_Angeles",
  created: "2026-01-04T10:00:00.000Z",
  updated: "2026-01-04T10:00:00.000Z",
};

export const mockWorkspace: Workspace = {
  id: MOCK_WORKSPACE_ID,
  name: "Personal",
  avatarUrl: null,
  created: "2026-01-04T10:00:00.000Z",
  updated: "2026-01-04T10:00:00.000Z",
};

/** Small seeded PRNG so the data is stable between reloads. */
export function rng(seed: number) {
  let s = seed >>> 0;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 0xffffffff;
  };
}

const NOW = new Date("2026-10-24T09:00:00.000Z");
export const MOCK_NOW = NOW;

export function daysAgo(days: number, hour = 10): string {
  const d = new Date(NOW.getTime() - days * 86_400_000);
  d.setUTCHours(hour, 0, 0, 0);
  return d.toISOString();
}

export function hlc(iso: string, n = 0): string {
  return `${String(new Date(iso).getTime()).padStart(13, "0")}:${n.toString(16).padStart(4, "0")}:mock`;
}

interface Person {
  first: string;
  last: string;
  company: string;
  title: string;
  city: string;
  lat: number;
  lon: number;
  img: number;
  bio?: string;
  interests?: string[];
  school?: string;
  birthday?: [number, number];
}

/** A birthday a few days out from whenever the mock runs, so "Birthday soon" is always on show. */
function birthdayIn(days: number): [number, number] {
  const d = new Date(Date.now() + days * 86_400_000);
  return [d.getMonth() + 1, d.getDate()];
}

const PEOPLE: Person[] = [
  { first: "Maya", last: "Chen", company: "Northwind Labs", title: "Co-founder and CEO", city: "San Francisco", lat: 37.77, lon: -122.42, img: 47, interests: ["climbing", "jazz"], school: "Stanford", birthday: birthdayIn(3) },
  { first: "Daniel", last: "Okafor", company: "Brightline Capital", title: "Partner", city: "New York", lat: 40.71, lon: -74.01, img: 12, interests: ["cycling"], school: "Wharton", birthday: [11, 2] },
  { first: "Priya", last: "Natarajan", company: "Lumen Health", title: "Head of Product", city: "Seattle", lat: 47.61, lon: -122.33, img: 32, interests: ["pottery", "trail running"], school: "UW" },
  { first: "Tomás", last: "Herrera", company: "Freelance", title: "Brand designer", city: "Mexico City", lat: 19.43, lon: -99.13, img: 59, interests: ["typography"], birthday: [10, 30] },
  { first: "Grace", last: "Whitfield", company: "Atlas Robotics", title: "VP Engineering", city: "Boston", lat: 42.36, lon: -71.06, img: 25, school: "MIT" },
  { first: "Kenji", last: "Watanabe", company: "Sora Ventures", title: "Principal", city: "Tokyo", lat: 35.68, lon: 139.69, img: 53, interests: ["photography"] },
  { first: "Amara", last: "Diallo", company: "Open Ledger", title: "Founder", city: "London", lat: 51.51, lon: -0.13, img: 44, interests: ["chess"], birthday: [10, 25] },
  { first: "Lucas", last: "Moreau", company: "Pixel & Grain", title: "Creative director", city: "Paris", lat: 48.86, lon: 2.35, img: 14 },
  { first: "Hannah", last: "Lindqvist", company: "Fjord Analytics", title: "Data scientist", city: "Stockholm", lat: 59.33, lon: 18.07, img: 29, interests: ["sailing"] },
  { first: "Omar", last: "Haddad", company: "Cedar Legal", title: "Attorney", city: "Austin", lat: 30.27, lon: -97.74, img: 60 },
  { first: "Isabella", last: "Rossi", company: "Terra Foods", title: "COO", city: "Milan", lat: 45.46, lon: 9.19, img: 38, birthday: [11, 14] },
  { first: "Noah", last: "Bennett", company: "Northwind Labs", title: "Staff engineer", city: "San Francisco", lat: 37.77, lon: -122.42, img: 11, interests: ["board games", "climbing"] },
  { first: "Zara", last: "Ahmed", company: "Signal Bay", title: "Growth lead", city: "Toronto", lat: 43.65, lon: -79.38, img: 26 },
  { first: "Elijah", last: "Brooks", company: "Harbor Accounting", title: "CPA", city: "Chicago", lat: 41.88, lon: -87.63, img: 51 },
  { first: "Sofia", last: "Martins", company: "Lumen Health", title: "Clinical lead", city: "Lisbon", lat: 38.72, lon: -9.14, img: 45, birthday: [12, 3] },
  { first: "Ravi", last: "Patel", company: "Keystone Realty", title: "Broker", city: "Denver", lat: 39.74, lon: -104.99, img: 56 },
  { first: "Chloe", last: "Dubois", company: "Studio Ombre", title: "Photographer", city: "Montreal", lat: 45.5, lon: -73.57, img: 20, interests: ["film"] },
  { first: "Marcus", last: "Reid", company: "Brightline Capital", title: "Associate", city: "New York", lat: 40.71, lon: -74.01, img: 57 },
  { first: "Yuki", last: "Tanaka", company: "Independent", title: "Executive coach", city: "Los Angeles", lat: 34.05, lon: -118.24, img: 31 },
  { first: "Adewale", last: "Balogun", company: "Lagos Tech Hub", title: "Program director", city: "Lagos", lat: 6.52, lon: 3.38, img: 15 },
  { first: "Emma", last: "Fischer", company: "Atlas Robotics", title: "Recruiter", city: "Berlin", lat: 52.52, lon: 13.4, img: 24, birthday: [10, 24] },
  { first: "Diego", last: "Alvarez", company: "Cafe Sur", title: "Owner", city: "Buenos Aires", lat: -34.6, lon: -58.38, img: 50 },
  { first: "Nora", last: "Kelly", company: "Harbor Accounting", title: "Partner", city: "Dublin", lat: 53.35, lon: -6.26, img: 41 },
  { first: "Samuel", last: "Owens", company: "Granite Build", title: "General contractor", city: "Portland", lat: 45.52, lon: -122.68, img: 52 },
  { first: "Leila", last: "Farahani", company: "Sora Ventures", title: "Platform lead", city: "Dubai", lat: 25.2, lon: 55.27, img: 16 },
  { first: "Jonas", last: "Bauer", company: "Fjord Analytics", title: "CTO", city: "Munich", lat: 48.14, lon: 11.58, img: 13 },
  { first: "Ava", last: "Thompson", company: "Northwind Labs", title: "Head of People", city: "Oakland", lat: 37.8, lon: -122.27, img: 23 },
  { first: "Hugo", last: "Silva", company: "Freelance", title: "Fractional CFO", city: "São Paulo", lat: -23.55, lon: -46.63, img: 58 },
  { first: "Mei", last: "Lin", company: "Signal Bay", title: "Founder", city: "Singapore", lat: 1.35, lon: 103.82, img: 27, birthday: [10, 28] },
  { first: "Benjamin", last: "Cole", company: "Cedar Legal", title: "Paralegal", city: "Austin", lat: 30.27, lon: -97.74, img: 54 },
  { first: "Fatima", last: "Zahra", company: "Open Ledger", title: "Engineer", city: "London", lat: 51.51, lon: -0.13, img: 43 },
  { first: "Oliver", last: "Hart", company: "Pixel & Grain", title: "Motion designer", city: "Amsterdam", lat: 52.37, lon: 4.9, img: 55 },
  { first: "Camille", last: "Laurent", company: "Terra Foods", title: "Supply chain", city: "Lyon", lat: 45.76, lon: 4.84, img: 21 },
  { first: "Ibrahim", last: "Yilmaz", company: "Keystone Realty", title: "Agent", city: "Istanbul", lat: 41.01, lon: 28.98, img: 17 },
  { first: "Lily", last: "Nguyen", company: "Lumen Health", title: "Designer", city: "Seattle", lat: 47.61, lon: -122.33, img: 30 },
  { first: "Ethan", last: "Walsh", company: "Granite Build", title: "Estimator", city: "Portland", lat: 45.52, lon: -122.68, img: 18 },
  { first: "Aisha", last: "Bello", company: "Independent", title: "Grant writer", city: "Nairobi", lat: -1.29, lon: 36.82, img: 40 },
  { first: "Felix", last: "Nowak", company: "Atlas Robotics", title: "Firmware engineer", city: "Warsaw", lat: 52.23, lon: 21.01, img: 19 },
  { first: "Maya", last: "Chen", company: "", title: "", city: "", lat: 0, lon: 0, img: 46 },
  { first: "Dan", last: "Okafor", company: "Brightline", title: "", city: "New York", lat: 40.71, lon: -74.01, img: 12 },
];

export interface SeedInteraction {
  id: Id;
  type: InteractionType;
  date: string;
  contactIds: Id[];
  subject?: string;
  title?: string;
  startDatetime?: string;
  endDatetime?: string;
  messageCount?: number;
  outgoing?: boolean;
  isFromMe?: boolean;
}

export interface MockSeed {
  contacts: Contact[];
  profiles: Map<Id, ContactProfile>;
  statistics: Map<Id, ContactStatistics>;
  lookups: Lookup[];
  contactLookups: ContactLookup[];
  groups: Group[];
  contactGroups: { groupId: Id; contactId: Id }[];
  notes: Note[];
  reminders: Reminder[];
  cadences: ReconnectCadence[];
  milestones: MilestoneDate[];
  related: RelatedContact[];
  relatedLabels: RelatedContactLabel[];
  interactions: SeedInteraction[];
  integrations: Integration[];
  needs: Need[];
  services: Service[];
  coords: Map<Id, { lat: number; lon: number; city: string }>;
  starred: Set<Id>;
}

function synced(id: Id, created: string, updated = created) {
  return { id, workspaceId: MOCK_WORKSPACE_ID, hlc: hlc(updated), created, updated, deleted: null };
}

export function buildSeed(): MockSeed {
  const rand = rng(42);
  const contacts: Contact[] = [];
  const profiles = new Map<Id, ContactProfile>();
  const statistics = new Map<Id, ContactStatistics>();
  const lookups: Lookup[] = [];
  const contactLookups: ContactLookup[] = [];
  const coords = new Map<Id, { lat: number; lon: number; city: string }>();
  const interactions: SeedInteraction[] = [];
  const milestones: MilestoneDate[] = [];
  const starred = new Set<Id>();

  const integrations: Integration[] = [
    { ...synced("int-google", daysAgo(200)), userId: MOCK_USER_ID, capability: "email", provider: "google", remoteId: "you@example.com", name: "Google (you@example.com)", status: "active", isSyncing: false, firstSync: daysAgo(200), lastSync: daysAgo(0, 8), lastError: null, archived: false, metadata: {} },
    { ...synced("int-apple-contacts", daysAgo(180)), userId: MOCK_USER_ID, capability: "contacts", provider: "apple_contacts", remoteId: null, name: "Apple Contacts", status: "active", isSyncing: false, firstSync: daysAgo(180), lastSync: daysAgo(1), lastError: null, archived: false, metadata: {} },
    { ...synced("int-apple-messages", daysAgo(150)), userId: MOCK_USER_ID, capability: "messages", provider: "apple_messages", remoteId: null, name: "Messages", status: "needs_attention", isSyncing: false, firstSync: daysAgo(150), lastSync: daysAgo(12), lastError: "Full Disk Access was revoked", archived: false, metadata: {} },
    { ...synced("int-calendar", daysAgo(190)), userId: MOCK_USER_ID, capability: "calendar", provider: "apple_calendar", remoteId: null, name: "Calendar", status: "active", isSyncing: false, firstSync: daysAgo(190), lastSync: daysAgo(0, 7), lastError: null, archived: false, metadata: {} },
  ];

  PEOPLE.forEach((p, i) => {
    const id = `c-${String(i + 1).padStart(3, "0")}`;
    const created = daysAgo(400 - i * 7);
    const displayName = `${p.first} ${p.last}`;
    // Interaction volume falls off with index so tiers are spread.
    const volume = i < 6 ? 40 + Math.floor(rand() * 30) : i < 16 ? 10 + Math.floor(rand() * 15) : i < 30 ? 1 + Math.floor(rand() * 6) : 0;
    const isDuplicateStub = i >= 38;
    let lastDate: string | null = null;
    let firstDate: string | null = null;
    let emailCount = 0;
    let eventCount = 0;
    let messageCount = 0;
    let callCount = 0;
    for (let k = 0; k < volume; k++) {
      const age = Math.floor(rand() * (i < 6 ? 120 : 400));
      const date = daysAgo(age, 8 + Math.floor(rand() * 10));
      const r = rand();
      const type: InteractionType = r < 0.5 ? "email" : r < 0.75 ? "event" : r < 0.92 ? "message_day" : "call";
      const iid = `i-${id}-${k}`;
      if (type === "email") {
        emailCount++;
        interactions.push({ id: iid, type, date, contactIds: [id], subject: EMAIL_SUBJECTS[k % EMAIL_SUBJECTS.length], isFromMe: rand() > 0.5 });
      } else if (type === "event") {
        eventCount++;
        const end = new Date(new Date(date).getTime() + 45 * 60_000).toISOString();
        interactions.push({ id: iid, type, date, contactIds: [id], title: EVENT_TITLES[k % EVENT_TITLES.length], startDatetime: date, endDatetime: end });
      } else if (type === "message_day") {
        messageCount++;
        interactions.push({ id: iid, type, date, contactIds: [id], messageCount: 2 + Math.floor(rand() * 12) });
      } else {
        callCount++;
        interactions.push({ id: iid, type, date, contactIds: [id], outgoing: rand() > 0.5 });
      }
      if (!lastDate || date > lastDate) lastDate = date;
      if (!firstDate || date < firstDate) firstDate = date;
    }
    const score = Math.min(1, volume / 60 + (lastDate ? Math.max(0, 0.3 - (NOW.getTime() - new Date(lastDate).getTime()) / 86_400_000 / 365) : 0));
    const bucket: 1 | 2 | 3 | 4 = score >= 0.75 ? 1 : score >= 0.5 ? 2 : score >= 0.25 ? 3 : 4;
    const relationship = bucket === 1 ? 4 : bucket === 2 ? 3 : bucket === 3 ? 2 : volume > 0 ? 1 : 0;
    if (i < 5 || i === 10 || i === 14) starred.add(id);

    contacts.push({
      ...synced(id, created, lastDate ?? created),
      relationship: relationship as Contact["relationship"],
      relationshipPinned: false,
      isLocal: false,
      isArchived: i === 37,
      archivedAt: i === 37 ? daysAgo(3) : null,
      isMemorialized: false,
      sendBirthdayNotification: true,
      score,
      originSource: i % 3 === 0 ? "google" : i % 3 === 1 ? "apple_contacts" : "linkedin",
      linkedUserId: i === 0 || i === 6 || i === 28 ? `user-hub-${i}` : null,
      sharedByUserId: null,
      isPrivate: i === 9,
    });

    profiles.set(id, {
      contactId: id,
      workspaceId: MOCK_WORKSPACE_ID,
      firstName: p.first,
      lastName: p.last,
      displayName,
      sortFirstName: p.first.toLowerCase(),
      sortLastName: p.last.toLowerCase(),
      nickname: null,
      pronouns: i % 7 === 0 ? "she/her" : i % 7 === 3 ? "he/him" : null,
      headline: p.title && p.company ? `${p.title} at ${p.company}` : p.title || null,
      company: p.company ?? null,
      title: p.title ?? null,
      location: p.city ?? null,
      bio: isDuplicateStub ? null : BIOS[i % BIOS.length].replace("{first}", p.first),
      birthday: p.birthday ? { year: 1980 + (i % 15), month: p.birthday[0], day: p.birthday[1] } : null,
      workHistory: p.company
        ? [
            { company: p.company, title: p.title, startDate: `${2019 + (i % 5)}-0${1 + (i % 9)}`, endDate: null, isCurrent: true, location: p.city },
            ...(i % 2 === 0 ? [{ company: PREVIOUS_COMPANIES[i % PREVIOUS_COMPANIES.length], title: "Senior " + (p.title.split(" ").pop() ?? "lead"), startDate: `${2014 + (i % 4)}`, endDate: `${2019 + (i % 5)}`, isCurrent: false, location: null }] : []),
          ]
        : [],
      education: p.school ? [{ school: p.school, degree: "BSc", field: "Computer Science", startYear: 2008, endYear: 2012 }] : [],
      addresses: p.city ? [{ label: "home", street: null, city: p.city, region: null, postalCode: null, country: null, latitude: p.lat, longitude: p.lon }] : [],
      websites: i % 4 === 0 ? [`https://${p.last.toLowerCase()}.example.com`] : [],
      socialLinks: isDuplicateStub
        ? []
        : [
            { platform: "linkedin", url: `https://www.linkedin.com/in/${p.first.toLowerCase()}-${p.last.toLowerCase()}`, handle: `${p.first.toLowerCase()}-${p.last.toLowerCase()}` },
            ...(i % 3 === 0 ? [{ platform: "twitter" as const, url: `https://x.com/${p.first.toLowerCase()}${p.last.toLowerCase()}`, handle: `${p.first.toLowerCase()}${p.last.toLowerCase()}` }] : []),
          ],
      interests: p.interests ?? [],
      fields: i % 5 === 0 ? { "how we met": "Introduced by Daniel at the Brightline dinner" } : {},
      avatarId: `av-${id}`,
      avatarUrl: `https://i.pravatar.cc/150?img=${p.img}`,
      memorialized: null,
      dataUpdated: lastDate ?? created,
    });

    if (p.city) coords.set(id, { lat: p.lat, lon: p.lon, city: p.city });

    statistics.set(id, {
      workspaceId: MOCK_WORKSPACE_ID,
      userId: null,
      contactId: id,
      emailCount,
      eventCount,
      messageCount,
      callCount,
      firstEmailDate: firstDate,
      lastEmailDate: lastDate,
      firstEventDate: firstDate,
      lastEventDate: lastDate,
      nextEventDate: i === 1 || i === 4 ? daysAgo(-1, 15) : i === 2 ? daysAgo(0, 17) : null,
      firstMessageDate: firstDate,
      lastMessageDate: lastDate,
      firstCallDate: null,
      lastCallDate: null,
      firstInteractionDate: firstDate,
      lastInteractionDate: lastDate,
      score,
      totalMinutes: eventCount * 45,
      bucket,
      interactionType: "derived",
    });

    // Lookups: email, phone, linkedin, and a provider id.
    const email = `${p.first.toLowerCase()}.${p.last.toLowerCase().replace(/\s+/g, "")}@${(p.company || "mail").toLowerCase().replace(/[^a-z]/g, "") || "mail"}.com`;
    const addLookup = (type: Lookup["type"], value: string, source: ContactLookup["source"], primary: boolean, label: string | null = null) => {
      const lid = `l-${id}-${type}-${lookups.length}`;
      lookups.push({ id: lid, workspaceId: MOCK_WORKSPACE_ID, type, value });
      contactLookups.push({ ...synced(`cl-${lid}`, created), lookupId: lid, contactId: id, source, isPrimary: primary, label, score: null, isInternal: false, parentId: null, userId: null });
    };
    addLookup("email", email, i % 3 === 0 ? "google" : "apple_contacts", true, "work");
    if (i % 2 === 0) addLookup("email", `${p.first.toLowerCase()}${i}@gmail.com`, "google", false, "personal");
    if (i % 3 !== 1) addLookup("phone", `+1415555${String(1000 + i).slice(-4)}`, "apple_contacts", true, "mobile");
    if (!isDuplicateStub) addLookup("linkedin", `${p.first.toLowerCase()}-${p.last.toLowerCase()}`, "linkedin", true);
    if (i % 3 === 1) addLookup("apple_contacts_id", `ABC${1000 + i}`, "apple_contacts", true);
    if (i === 38) addLookup("email", "maya.chen@northwindlabs.com", "csv", false);

    if (p.birthday) {
      milestones.push({ ...synced(`ms-${id}`, created), contactId: id, dateType: "birthday", year: 1980 + (i % 15), month: p.birthday[0], day: p.birthday[1], description: null, notify: true, system: true, nextOccurrence: nextOccurrence(p.birthday[0], p.birthday[1]), lastOccurrence: null });
    }
  });

  // Upcoming events in the near future.
  interactions.push(
    { id: "i-up-1", type: "event", date: daysAgo(-1, 15), contactIds: ["c-002", "c-018"], title: "Brightline portfolio review", startDatetime: daysAgo(-1, 15), endDatetime: daysAgo(-1, 16) },
    { id: "i-up-2", type: "event", date: daysAgo(0, 17), contactIds: ["c-003"], title: "Coffee with Priya", startDatetime: daysAgo(0, 17), endDatetime: daysAgo(0, 18) },
    { id: "i-up-3", type: "event", date: daysAgo(-3, 11), contactIds: ["c-005", "c-012"], title: "Atlas hiring sync", startDatetime: daysAgo(-3, 11), endDatetime: daysAgo(-3, 12) },
  );
  // Meetings and threads with several people on them, so the connections map has something to draw.
  for (let k = 0; k < 6; k++) interactions.push({ id: `i-nw-${k}`, type: "event", date: daysAgo(7 * k + 2, 9), contactIds: ["c-001", "c-012", "c-027", "c-009"], title: "Northwind weekly", startDatetime: daysAgo(7 * k + 2, 9), endDatetime: daysAgo(7 * k + 2, 10) });
  for (let k = 0; k < 3; k++) interactions.push({ id: `i-design-${k}`, type: "event", date: daysAgo(14 * k + 5, 14), contactIds: ["c-004", "c-035", "c-017", "c-001"], title: "Design review", startDatetime: daysAgo(14 * k + 5, 14), endDatetime: daysAgo(14 * k + 5, 15) });
  for (let k = 0; k < 2; k++) interactions.push({ id: `i-board-${k}`, type: "event", date: daysAgo(30 * k + 12, 16), contactIds: ["c-001", "c-002", "c-018"], title: "Board prep", startDatetime: daysAgo(30 * k + 12, 16), endDatetime: daysAgo(30 * k + 12, 17) });
  for (let k = 0; k < 3; k++) interactions.push({ id: `i-inv-${k}`, type: "email", date: daysAgo(10 * k + 4, 11), contactIds: ["c-002", "c-006", "c-018", "c-025"], subject: "Investor update", isFromMe: k === 0 });
  for (let k = 0; k < 2; k++) interactions.push({ id: `i-plan-${k}`, type: "email", date: daysAgo(5 * k + 3, 12), contactIds: ["c-001", "c-003", "c-012"], subject: "Q4 planning", isFromMe: k === 1 });
  interactions.push({ id: "i-atlas-1", type: "email", date: daysAgo(6, 15), contactIds: ["c-005", "c-012", "c-001"], subject: "Atlas hiring plan", isFromMe: false });

  const groups: Group[] = [
    { ...synced("g-fav", daysAgo(400)), parentId: null, createdByUserId: MOCK_USER_ID, name: "Favorites", description: "Your starred people.", kind: "static", filter: null, color: "#d946ef", slug: "favorites", isInternal: true, sortOrder: "0", reconnectCadence: null },
    { ...synced("g-investors", daysAgo(300)), parentId: null, createdByUserId: MOCK_USER_ID, name: "Investors", description: "People who have invested or might.", kind: "static", filter: null, color: "#2563eb", slug: "investors", isInternal: false, sortOrder: "1", reconnectCadence: "quarterly" },
    { ...synced("g-northwind", daysAgo(250)), parentId: null, createdByUserId: MOCK_USER_ID, name: "Northwind team", description: null, kind: "smart", filter: "company:northwind", color: "#16a34a", slug: "northwind", isInternal: false, sortOrder: "2", reconnectCadence: null },
    { ...synced("g-design", daysAgo(220)), parentId: null, createdByUserId: MOCK_USER_ID, name: "Designers", description: "Design and creative people I could hire or refer.", kind: "static", filter: null, color: "#d97706", slug: "designers", isInternal: false, sortOrder: "3", reconnectCadence: null },
    { ...synced("g-bay", daysAgo(100)), parentId: null, createdByUserId: MOCK_USER_ID, name: "Bay Area", description: "Everyone around San Francisco.", kind: "smart", filter: 'location:"san francisco"', color: "#0891b2", slug: "bay-area", isInternal: false, sortOrder: "4", reconnectCadence: null },
  ];
  const contactGroups = [
    ...Array.from(starred).map((contactId) => ({ groupId: "g-fav", contactId })),
    { groupId: "g-investors", contactId: "c-002" },
    { groupId: "g-investors", contactId: "c-006" },
    { groupId: "g-investors", contactId: "c-018" },
    { groupId: "g-investors", contactId: "c-025" },
    { groupId: "g-design", contactId: "c-004" },
    { groupId: "g-design", contactId: "c-008" },
    { groupId: "g-design", contactId: "c-017" },
    { groupId: "g-design", contactId: "c-032" },
    { groupId: "g-design", contactId: "c-035" },
  ];

  const notes: Note[] = [
    mkNote("n-1", "c-001", daysAgo(4), "Maya is raising a seed round in Q1. Wants intros to climate-focused funds. Follow up next Tuesday.", "fundraising", "#2563eb"),
    mkNote("n-2", "c-001", daysAgo(40), "Met at the Northwind offsite. She mentioned they are hiring a design lead.", null, null),
    mkNote("n-3", "c-002", daysAgo(9), "Daniel is interested in B2B tooling. Send him the deck once the numbers are final.", "investor", "#16a34a"),
    mkNote("n-4", "c-003", daysAgo(2), "Priya wants a recommendation for a pediatric clinic designer. Remind me on Nov 3.", "ask", "#d97706"),
    mkNote("n-5", "c-004", daysAgo(60), "Tomás did our brand refresh. Rates: 120/hr. Reliable and fast.", "vendor", null),
    mkNote("n-6", "c-007", daysAgo(15), "Amara is looking for a fractional CFO for Open Ledger. Hugo could be a fit.", "ask", "#d97706"),
    mkNote("n-7", "c-012", daysAgo(21), "Noah is thinking about leaving Northwind. Wants to talk about robotics roles.", null, null),
    mkNote("n-8", "c-005", daysAgo(5), "Grace: Atlas is hiring two firmware engineers in Berlin. Felix may be interested.", "hiring", "#7c3aed"),
    mkNote("n-9", "c-019", daysAgo(90), "Yuki recommended the book on managing up. Thank her next time.", null, null),
    { ...synced("n-10", daysAgo(30)), contactId: null, groupId: "g-investors", interactionId: null, authorUserId: MOCK_USER_ID, content: "Quarterly update goes out the first week of each quarter.", plainText: "Quarterly update goes out the first week of each quarter.", label: "process", color: null, icon: null, providerId: null },
  ];

  const reminders: Reminder[] = [
    mkReminder("r-1", "c-001", daysAgo(-2, 9), "Follow up on climate fund intros"),
    mkReminder("r-2", "c-003", "2026-11-03T09:00:00.000Z", "Send clinic designer recommendation"),
    mkReminder("r-3", "c-002", daysAgo(1, 9), "Send Daniel the deck"),
    mkReminder("r-4", "c-007", daysAgo(-6, 9), "Check whether Amara found a CFO"),
    mkReminder("r-5", "c-019", daysAgo(20, 9), "Thank Yuki for the book", true),
    mkReminder("r-6", null, daysAgo(0, 14), "Renew domain for the side project"),
  ];

  const cadences: ReconnectCadence[] = [
    { ...synced("cad-1", daysAgo(100)), contactId: "c-002", cadence: "monthly", qualifier: "first", weekdayNumber: 2, monthNumber: null, dayNumber: null, isDisabled: false, isAutomatic: false, isCustom: true },
    { ...synced("cad-2", daysAgo(90)), contactId: "c-007", cadence: "quarterly", qualifier: null, weekdayNumber: null, monthNumber: null, dayNumber: null, isDisabled: false, isAutomatic: false, isCustom: false },
    { ...synced("cad-3", daysAgo(80)), contactId: "c-010", cadence: null, qualifier: null, weekdayNumber: null, monthNumber: null, dayNumber: null, isDisabled: true, isAutomatic: false, isCustom: null },
  ];

  const related: RelatedContact[] = [
    { ...synced("rel-1", daysAgo(200)), contactId: "c-001", relatedContactId: "c-012" },
    { ...synced("rel-2", daysAgo(200)), contactId: "c-012", relatedContactId: "c-001" },
    { ...synced("rel-3", daysAgo(150)), contactId: "c-002", relatedContactId: "c-018" },
    { ...synced("rel-4", daysAgo(150)), contactId: "c-018", relatedContactId: "c-002" },
    { ...synced("rel-5", daysAgo(120)), contactId: "c-001", relatedContactId: "c-027" },
    { ...synced("rel-6", daysAgo(120)), contactId: "c-027", relatedContactId: "c-001" },
  ];
  const relatedLabels: RelatedContactLabel[] = [
    { ...synced("rl-1", daysAgo(200)), contactId: "c-001", relatedContactId: "c-012", label: "colleague" },
    { ...synced("rl-2", daysAgo(200)), contactId: "c-012", relatedContactId: "c-001", label: "manager" },
    { ...synced("rl-3", daysAgo(150)), contactId: "c-002", relatedContactId: "c-018", label: "colleague" },
    { ...synced("rl-4", daysAgo(150)), contactId: "c-018", relatedContactId: "c-002", label: "manager" },
    { ...synced("rl-5", daysAgo(120)), contactId: "c-001", relatedContactId: "c-027", label: "introduced-by" },
  ];

  const needs: Need[] = [
    { ...synced("need-1", daysAgo(4)), contactId: "c-001", title: "Intros to climate-focused seed funds", description: "Raising a seed round in Q1, looking for funds that back climate tooling.", keywords: ["seed", "climate", "investor", "fundraising"], keywordsGeneratedByAi: false, category: "Fundraising", timeline: "Q1", location: "San Francisco", isCompleted: false, completedAt: null, visibility: "connections" },
    { ...synced("need-2", daysAgo(15)), contactId: "c-007", title: "Fractional CFO", description: "Part-time finance lead for a 12 person fintech.", keywords: ["cfo", "finance", "fractional"], keywordsGeneratedByAi: false, category: "Finance", timeline: "ASAP", location: "London", isCompleted: false, completedAt: null, visibility: "network" },
    { ...synced("need-3", daysAgo(2)), contactId: "c-003", title: "Designer for a pediatric clinic", description: "Interior and brand design for a new clinic.", keywords: ["design", "clinic", "interior"], keywordsGeneratedByAi: false, category: "Design", timeline: "this quarter", location: "Seattle", isCompleted: false, completedAt: null, visibility: "connections" },
    { ...synced("need-4", daysAgo(60)), contactId: "c-012", title: "Robotics roles", description: "Exploring staff engineer roles in robotics.", keywords: ["robotics", "engineer", "job"], keywordsGeneratedByAi: false, category: "Hiring", timeline: null, location: null, isCompleted: true, completedAt: daysAgo(10), visibility: "private" },
  ];
  const services: Service[] = [
    { ...synced("svc-1", daysAgo(100)), contactId: "c-004", title: "Brand and identity design", shortDescription: "Logos, typography systems and brand guidelines for early stage companies.", longDescription: null, keywords: ["brand", "logo", "design", "typography"], keywordsGeneratedByAi: false, category: "Design", location: "Mexico City", isActive: true, visibility: "network" },
    { ...synced("svc-2", daysAgo(80)), contactId: "c-028", title: "Fractional CFO", shortDescription: "Finance leadership for seed to series B companies.", longDescription: null, keywords: ["cfo", "finance", "fractional", "fundraising"], keywordsGeneratedByAi: false, category: "Finance", location: "São Paulo", isActive: true, visibility: "network" },
    { ...synced("svc-3", daysAgo(70)), contactId: "c-014", title: "Tax and accounting for startups", shortDescription: "Bookkeeping, tax filing and R&D credits.", longDescription: null, keywords: ["tax", "accounting", "cpa"], keywordsGeneratedByAi: false, category: "Finance", location: "Chicago", isActive: true, visibility: "connections" },
    { ...synced("svc-4", daysAgo(50)), contactId: "c-019", title: "Executive coaching", shortDescription: "Coaching for first-time founders and new managers.", longDescription: null, keywords: ["coaching", "leadership", "founder"], keywordsGeneratedByAi: false, category: "Coaching", location: "Los Angeles", isActive: true, visibility: "network" },
    { ...synced("svc-5", daysAgo(40)), contactId: "c-002", title: "Seed investing", shortDescription: "Leads seed rounds in B2B software and climate tooling.", longDescription: null, keywords: ["seed", "investor", "climate", "b2b"], keywordsGeneratedByAi: false, category: "Fundraising", location: "New York", isActive: true, visibility: "connections" },
  ];

  return { contacts, profiles, statistics, lookups, contactLookups, groups, contactGroups, notes, reminders, cadences, milestones, related, relatedLabels, interactions, integrations, needs, services, coords, starred };
}

function mkNote(id: Id, contactId: Id, created: string, text: string, label: string | null, color: string | null): Note {
  return { ...synced(id, created), contactId, groupId: null, interactionId: null, authorUserId: MOCK_USER_ID, content: text, plainText: text, label, color, icon: null, providerId: null };
}

function mkReminder(id: Id, contactId: Id | null, fireAt: string, title: string, processed = false): Reminder {
  return { ...synced(id, daysAgo(30)), userId: MOCK_USER_ID, contactId, noteId: null, reconnectCadenceId: null, title, fireAt, isProcessed: processed, processedAt: processed ? fireAt : null };
}

export function nextOccurrence(month: number, day: number, from: Date = NOW): string {
  const y = from.getUTCFullYear();
  // Noon UTC so the calendar date survives display in any timezone.
  let d = new Date(Date.UTC(y, month - 1, day, 12));
  if (d.getTime() < from.getTime() - 86_400_000) d = new Date(Date.UTC(y + 1, month - 1, day, 12));
  return d.toISOString();
}

const EMAIL_SUBJECTS = ["Re: next steps", "Intro: you two should meet", "Deck v3", "Thursday?", "Notes from our call", "Quick question", "Following up", "Invoice 0231", "Re: hiring plan", "Offsite agenda"];
const EVENT_TITLES = ["Coffee", "Catch up", "Product review", "Board prep", "Lunch", "Design critique", "1:1", "Dinner", "Pitch practice", "Walk and talk"];
const PREVIOUS_COMPANIES = ["Stripe", "Figma", "Shopify", "Notion", "Canva", "Airtable"];
const BIOS = [
  "{first} builds developer tools and writes about distributed systems.",
  "{first} has spent a decade in early stage investing with a focus on infrastructure.",
  "{first} leads product for a healthcare company and mentors first-time PMs.",
  "{first} runs an independent studio working with founders on brand and identity.",
  "{first} spent eight years in robotics before moving into engineering leadership.",
];
